import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Check,
  AlertTriangle,
  Loader2,
  Info,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { MatchRateTablePreview } from "./MatchRateTablePreview";
import { BrowseAlternatesList } from "./BrowseAlternatesList";

export type LibraryMatchStatus =
  | "searching"
  | "found"
  | "confirm"
  | "not_found"
  | "no_library_data";

export interface LibraryMatchCandidate {
  wbId: string;
  name: string;
  hint?: string;
}

interface RateEntry {
  age: number;
  ageRange: string;
  rate: number;
  tobacco?: boolean;
}

interface Props {
  status: LibraryMatchStatus | undefined;
  carrierName?: string;
  matchedWbId?: string | null;
  matchedWbName?: string | null;
  candidates?: LibraryMatchCandidate[];
  stateLabel?: string | null;
  /** Raw library rate payload, supplied only when status === "found" and the
   *  backend included it (Found-only). Used by the "Show rate table" disclosure. */
  rates?: RateEntry[] | null;
  /** Which rating area + effective month the rates actually represent, and
   *  whether they came from the cache or a live W&B fetch. "unavailable" means
   *  no rate exists for the broker's region/month — show a warning, not rates. */
  ratesRatingAreaId?: string | null;
  ratesEffectiveMonth?: string | null;
  ratesSource?: "cache" | "live" | "unavailable" | null;
  ratesReason?: string | null;
  isRefining?: boolean;
  /** Lazy-fetch the full carrier catalog for browse-all-plans UX. */
  listCarrierLibrary: () => Promise<LibraryMatchCandidate[]>;
  /** Fire a refine search with the broker's free-text query. Currently
   *  unused in browse-list world but kept on the interface for callers
   *  that haven't migrated yet. */
  onRefine: (query: string) => void;
  /** Broker picked a candidate from any browse list. */
  onPickCandidate: (cand: LibraryMatchCandidate) => void;
  /** Broker hit "Enter rates manually" from the not_found exit. */
  onSwitchToManual: () => void;
  /** Broker hit "Skip · add later" — defer this plan. */
  onDefer: () => void;
  /** Broker hit "Unlink" on a found state — clear the match. */
  onUnlink: () => void;
}

export function MatchPanel({
  status,
  carrierName,
  matchedWbId,
  matchedWbName,
  candidates,
  stateLabel,
  rates,
  ratesRatingAreaId,
  ratesEffectiveMonth,
  ratesSource,
  ratesReason,
  isRefining,
  listCarrierLibrary,
  onRefine,
  onPickCandidate,
  onSwitchToManual,
  onDefer,
  onUnlink,
}: Props) {
  if (!status || status === "searching") return <SearchingPanel />;
  if (status === "found")
    return (
      <FoundPanel
        carrierName={carrierName ?? ""}
        matchedWbId={matchedWbId ?? null}
        matchedWbName={matchedWbName ?? ""}
        rates={rates}
        ratesRatingAreaId={ratesRatingAreaId ?? null}
        ratesEffectiveMonth={ratesEffectiveMonth ?? null}
        ratesSource={ratesSource ?? null}
        ratesReason={ratesReason ?? null}
        listCarrierLibrary={listCarrierLibrary}
        onPickCandidate={onPickCandidate}
        onSwitchToManual={onSwitchToManual}
        onDefer={onDefer}
        onUnlink={onUnlink}
      />
    );
  if (status === "confirm")
    return (
      <BrowseListPanel
        tone="amber"
        title="Confirm match"
        helper="Multiple library plans look close. Pick the right one."
        carrierName={carrierName ?? ""}
        matchedWbId={null}
        listCarrierLibrary={listCarrierLibrary}
        onPickCandidate={onPickCandidate}
        onSwitchToManual={onSwitchToManual}
        onDefer={onDefer}
      />
    );
  if (status === "not_found")
    return (
      <BrowseListPanel
        tone="red"
        title="We couldn't auto-match this plan"
        helper="Pick from the carrier's library or switch to flat tier rates."
        carrierName={carrierName ?? ""}
        matchedWbId={null}
        listCarrierLibrary={listCarrierLibrary}
        onPickCandidate={onPickCandidate}
        onSwitchToManual={onSwitchToManual}
        onDefer={onDefer}
      />
    );
  if (status === "no_library_data")
    return <NoLibraryDataPanel stateLabel={stateLabel} />;
  return null;
}

// ── Searching ───────────────────────────────────────────────────────────

function SearchingPanel() {
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-[8px] px-4 py-4 flex items-center gap-3 text-[12.5px] text-stone-500">
      <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
      <span>Looking up in library…</span>
    </div>
  );
}

// ── Found ───────────────────────────────────────────────────────────────

function FoundPanel({
  carrierName,
  matchedWbId,
  matchedWbName,
  rates,
  ratesRatingAreaId,
  ratesEffectiveMonth,
  ratesSource,
  ratesReason,
  listCarrierLibrary,
  onPickCandidate,
  onSwitchToManual,
  onDefer,
  onUnlink,
}: {
  carrierName: string;
  matchedWbId: string | null;
  matchedWbName: string;
  rates: RateEntry[] | null | undefined;
  ratesRatingAreaId: string | null;
  ratesEffectiveMonth: string | null;
  ratesSource: "cache" | "live" | "unavailable" | null;
  ratesReason: string | null;
  listCarrierLibrary: () => Promise<LibraryMatchCandidate[]>;
  onPickCandidate: (cand: LibraryMatchCandidate) => void;
  onSwitchToManual: () => void;
  onDefer: () => void;
  onUnlink: () => void;
}) {
  const [showTable, setShowTable] = useState(false);
  const [showBrowse, setShowBrowse] = useState(false);
  const tierCount = rates
    ? new Set(
        rates.map((r) => {
          const asRecord = r as unknown as Record<string, unknown>;
          if (typeof asRecord.ageRange === "string") return asRecord.ageRange;
          if (typeof asRecord.AgeRange === "string") return asRecord.AgeRange;
          return String(asRecord.age ?? asRecord.AgeFrom ?? "");
        }),
      ).size
    : 0;
  // YYYY-MM-01 → "YYYY-MM" for display.
  const monthLabel = ratesEffectiveMonth ? ratesEffectiveMonth.slice(0, 7) : null;
  const regionLabel = ratesRatingAreaId ? `Area ${ratesRatingAreaId}` : null;
  const sourceLabel =
    ratesSource === "live"
      ? "fetched live from Word & Brown"
      : ratesSource === "cache"
        ? "from library"
        : null;
  // Provenance suffix: "· Area 5 · eff 2026-01 · fetched live from Word & Brown".
  const provenance = [regionLabel, monthLabel ? `eff ${monthLabel}` : null, sourceLabel]
    .filter(Boolean)
    .join(" · ");

  // Matched plan, but W&B has no rate for the broker's region+month. Don't
  // show borrowed rates — warn and push toward manual entry.
  if (ratesSource === "unavailable") {
    return (
      <div className="bg-amber-50 border border-amber-300 rounded-[8px] px-4 py-3 flex flex-col gap-2 text-amber-900">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-amber-700 flex-shrink-0" />
          <span className="text-[13px] font-medium flex-1 truncate">
            Found in library · {matchedWbName}
          </span>
        </div>
        <div className="flex items-start gap-2 text-[11.5px] text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-amber-600" />
          <span>
            No rates for {regionLabel ?? "this region"}
            {monthLabel ? ` · eff ${monthLabel}` : ""}.{" "}
            {ratesReason ?? "The library has no cached rate for this region and effective date."}{" "}
            Plan link kept — enter rates manually or pick another plan.
          </span>
        </div>
        <div className="flex items-center justify-end gap-1 pt-0.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[11.5px] text-amber-800 hover:bg-amber-100/60"
            onClick={onUnlink}
          >
            Unlink
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-[11.5px] border-amber-300 text-amber-900 hover:bg-amber-100/60"
            onClick={onSwitchToManual}
          >
            Enter rates manually
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-[8px] px-4 py-3 flex flex-col gap-2 text-emerald-900">
      <div className="flex items-center gap-2">
        <Check className="h-4 w-4 text-emerald-700 flex-shrink-0" />
        <span className="text-[13px] font-medium flex-1 truncate">
          Found in library · {matchedWbName}
        </span>
      </div>
      <div className="flex items-center justify-between text-[11.5px]">
        <span className="text-emerald-800">
          {tierCount > 0
            ? `Rates · ${tierCount} age band${tierCount === 1 ? "" : "s"}${provenance ? ` · ${provenance}` : " from library catalog"}`
            : "Rates · pulled from library catalog at confirm"}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowBrowse((v) => !v)}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11.5px] font-medium text-stone-600 hover:bg-stone-100 hover:text-stone-900"
          >
            {showBrowse ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {showBrowse ? "Hide alternates" : "Browse alternates"}
          </button>
          {Array.isArray(rates) && rates.length > 0 && (
            <button
              type="button"
              onClick={() => setShowTable((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11.5px] font-medium text-emerald-800 hover:bg-emerald-100/60 hover:text-emerald-900"
            >
              {showTable ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              {showTable ? "Hide rate table" : "Show rate table"}
            </button>
          )}
        </div>
      </div>
      {showTable && Array.isArray(rates) && rates.length > 0 && (
        <div className="mt-1">
          <MatchRateTablePreview rates={rates} />
        </div>
      )}
      {showBrowse && (
        <div className="mt-1 space-y-2">
          <BrowseAlternatesList
            carrierName={carrierName}
            matchedWbId={matchedWbId}
            listCarrierLibrary={listCarrierLibrary}
            onPickCandidate={(c) => {
              onPickCandidate(c);
              setShowBrowse(false);
            }}
          />
          <div className="flex items-center justify-end gap-1 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11.5px] text-stone-700 hover:bg-emerald-100/60"
              onClick={() => {
                onUnlink();
                setShowBrowse(false);
              }}
            >
              Unlink
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11.5px] text-stone-700 hover:bg-emerald-100/60"
              onClick={() => {
                onDefer();
                setShowBrowse(false);
              }}
            >
              Skip · add later
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[11.5px]"
              onClick={() => {
                onSwitchToManual();
                setShowBrowse(false);
              }}
            >
              Enter rates manually
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Browse list (confirm + not_found share this) ──────────────────────

function BrowseListPanel({
  tone,
  title,
  helper,
  carrierName,
  matchedWbId,
  listCarrierLibrary,
  onPickCandidate,
  onSwitchToManual,
  onDefer,
}: {
  tone: "amber" | "red";
  title: string;
  helper: string;
  carrierName: string;
  matchedWbId: string | null;
  listCarrierLibrary: () => Promise<LibraryMatchCandidate[]>;
  onPickCandidate: (cand: LibraryMatchCandidate) => void;
  onSwitchToManual: () => void;
  onDefer: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<LibraryMatchCandidate[]>([]);
  // Fetch once on mount — see comment in BrowseAlternatesList. Depending
  // on the callback reference would re-fetch every render.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listCarrierLibrary()
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const palette =
    tone === "amber"
      ? {
          bg: "bg-amber-50",
          border: "border-amber-300",
          text: "text-amber-900",
          icon: "text-amber-700",
          helperText: "text-amber-700",
          rowBorder: "border-amber-200",
          rowHover: "hover:bg-amber-50/40",
          ctaBtn: "border-amber-300 text-amber-900 hover:bg-amber-100/60",
          ghostBtn: "text-amber-800 hover:text-amber-900 hover:bg-amber-100/60",
        }
      : {
          bg: "bg-red-50",
          border: "border-red-200",
          text: "text-red-900",
          icon: "text-red-700",
          helperText: "text-red-700",
          rowBorder: "border-red-200",
          rowHover: "hover:bg-red-50/40",
          ctaBtn: "border-red-300 text-red-900 hover:bg-red-100/60",
          ghostBtn: "text-red-800 hover:text-red-900 hover:bg-red-100/60",
        };
  const others = items.filter((c) => c.wbId !== matchedWbId);
  const carrierLabel = carrierName?.trim() || "carrier";

  return (
    <div
      className={cn(
        "rounded-[8px] border px-4 py-3 flex flex-col gap-3",
        palette.bg,
        palette.border,
        palette.text,
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          className={cn("h-4 w-4 flex-shrink-0 mt-0.5", palette.icon)}
        />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium">{title}</div>
          <div
            className={cn("text-[11.5px] mt-0.5", palette.helperText)}
          >
            {helper}
          </div>
        </div>
      </div>

      <div
        className={cn(
          "rounded-md border bg-white",
          tone === "amber" ? "border-amber-200" : "border-red-200",
        )}
      >
        <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-stone-500 border-b border-stone-100">
          All {carrierLabel} plans · {loading ? "…" : others.length}
        </div>
        <div className="max-h-[260px] overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-6 text-[12px] text-stone-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-stone-400" />
              Loading library…
            </div>
          )}
          {!loading && others.length === 0 && (
            <div className="px-3 py-6 text-[12px] italic text-stone-500">
              No library matches for {carrierLabel}.
            </div>
          )}
          {!loading && others.length > 0 && (
            <ul className="divide-y divide-stone-100">
              {others.map((c) => (
                <li key={c.wbId}>
                  <button
                    type="button"
                    onClick={() => onPickCandidate(c)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-stone-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px] font-medium text-stone-900">
                        {c.name}
                      </div>
                      {c.hint && (
                        <div className="truncate text-[10.5px] text-stone-500">
                          {c.hint}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 justify-end pt-1">
        <Button
          variant="ghost"
          size="sm"
          className={cn("h-7 text-[12px]", palette.ghostBtn)}
          onClick={onDefer}
        >
          Skip · add later
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-7 text-[12px]", palette.ctaBtn)}
          onClick={onSwitchToManual}
        >
          Enter rates manually
        </Button>
      </div>
    </div>
  );
}

// ── No library data ─────────────────────────────────────────────────────

function NoLibraryDataPanel({ stateLabel }: { stateLabel?: string | null }) {
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-[8px] px-4 py-3 flex items-start gap-2 text-stone-700">
      <Info className="h-4 w-4 text-stone-400 flex-shrink-0 mt-0.5" />
      <div className="text-[12.5px] leading-relaxed">
        Library not yet available for {stateLabel ?? "this state"}. Rates will be added in the plan
        editor after migration. This plan is auto-deferred for now — you can continue.
      </div>
    </div>
  );
}

